import type p5 from 'p5';
import type { PoseData, VisualConfig } from '../../../types';
import { distance } from '../../../utils/geometry';

/**
 * Renders a stick figure from pose data using p5.js
 */
export class StickFigureRenderer {
  /**
   * Draw torso as filled shape with triangle top and full/assumed outline
   */
  private drawTorso(
    p: p5,
    landmarks: PoseData['landmarks'],
    width: number,
    height: number,
    config: VisualConfig,
    isDebugMode: boolean
  ): void {
    // Check if shoulders are visible
    const leftShoulderVisible = landmarks.leftShoulder.visibility >= 0.5;
    const rightShoulderVisible = landmarks.rightShoulder.visibility >= 0.5;
    
    if (!leftShoulderVisible || !rightShoulderVisible) {
      return; // Cannot draw torso without both shoulders
    }

    // Convert shoulder positions to screen coordinates
    const leftShoulderX = landmarks.leftShoulder.x * width;
    const leftShoulderY = landmarks.leftShoulder.y * height;
    const rightShoulderX = landmarks.rightShoulder.x * width;
    const rightShoulderY = landmarks.rightShoulder.y * height;

    // Calculate shoulder midpoint and width
    const shoulderMidX = (leftShoulderX + rightShoulderX) / 2;
    const shoulderMidY = (leftShoulderY + rightShoulderY) / 2;
    const shoulderWidth = Math.abs(rightShoulderX - leftShoulderX);

    // Calculate triangle point above shoulders (0.15x shoulder width upward)
    const trianglePointX = shoulderMidX;
    const trianglePointY = shoulderMidY - (shoulderWidth * 0.15);

    // Check hip visibility
    const leftHipVisible = landmarks.leftHip.visibility >= 0.5;
    const rightHipVisible = landmarks.rightHip.visibility >= 0.5;
    const bothHipsVisible = leftHipVisible && rightHipVisible;
    const oneHipVisible = leftHipVisible !== rightHipVisible;

    // Set fill style
    if (isDebugMode) {
      p.noFill();
    } else {
      p.fill(config.primaryColor.r, config.primaryColor.g, config.primaryColor.b, config.primaryColor.a * 255);
    }
    p.stroke(config.primaryColor.r, config.primaryColor.g, config.primaryColor.b, config.primaryColor.a * 255);
    p.strokeWeight(config.stickFigureThickness);

    // Draw torso outline
    p.beginShape();
    
    // Start with triangle point at top
    p.vertex(trianglePointX, trianglePointY);
    
    // Left shoulder
    p.vertex(leftShoulderX, leftShoulderY);

    if (bothHipsVisible) {
      // Full torso outline with visible hips
      const leftHipX = landmarks.leftHip.x * width;
      const leftHipY = landmarks.leftHip.y * height;
      const rightHipX = landmarks.rightHip.x * width;
      const rightHipY = landmarks.rightHip.y * height;
      
      p.vertex(leftHipX, leftHipY);
      p.vertex(rightHipX, rightHipY);
    } else if (oneHipVisible) {
      // Partial hip visibility - use visible hip and calculate assumed position for the other
      if (leftHipVisible) {
        const leftHipX = landmarks.leftHip.x * width;
        const leftHipY = landmarks.leftHip.y * height;
        
        // Calculate assumed right hip position (symmetric to left hip)
        const hipOffset = leftHipX - shoulderMidX;
        const assumedRightHipX = shoulderMidX - hipOffset;
        const assumedRightHipY = leftHipY; // Same Y as visible hip
        
        p.vertex(leftHipX, leftHipY);
        p.vertex(assumedRightHipX, assumedRightHipY);
      } else {
        // Right hip visible, left hip not visible
        const rightHipX = landmarks.rightHip.x * width;
        const rightHipY = landmarks.rightHip.y * height;
        
        // Calculate assumed left hip position (symmetric to right hip)
        const hipOffset = rightHipX - shoulderMidX;
        const assumedLeftHipX = shoulderMidX - hipOffset;
        const assumedLeftHipY = rightHipY; // Same Y as visible hip
        
        p.vertex(assumedLeftHipX, assumedLeftHipY);
        p.vertex(rightHipX, rightHipY);
      }
    } else {
      // No hips visible - draw assumed torso outline
      // Assumed hip width: ~0.7x shoulder width
      // Assumed torso length: ~1.5x shoulder width
      const assumedHipWidth = shoulderWidth * 0.7;
      const assumedTorsoLength = shoulderWidth * 1.5;
      
      // Calculate assumed hip positions with natural taper
      const assumedHipY = shoulderMidY + assumedTorsoLength;
      const assumedLeftHipX = shoulderMidX - (assumedHipWidth / 2);
      const assumedRightHipX = shoulderMidX + (assumedHipWidth / 2);
      
      p.vertex(assumedLeftHipX, assumedHipY);
      p.vertex(assumedRightHipX, assumedHipY);
    }
    
    // Right shoulder
    p.vertex(rightShoulderX, rightShoulderY);
    
    p.endShape(p.CLOSE);
    
    // Reset fill
    p.noFill();
  }

  /**
   * Draw a line between two landmarks if both are visible
   */
  private drawConnection(
    p: p5,
    x1: number,
    y1: number,
    visibility1: number,
    x2: number,
    y2: number,
    visibility2: number,
    width: number,
    height: number
  ): void {
    // Skip drawing if either landmark has low visibility
    if (visibility1 < 0.5 || visibility2 < 0.5) {
      return;
    }

    // Convert normalized coordinates to screen coordinates
    const screenX1 = x1 * width;
    const screenY1 = y1 * height;
    const screenX2 = x2 * width;
    const screenY2 = y2 * height;

    p.line(screenX1, screenY1, screenX2, screenY2);
  }

  /**
   * Render the stick figure
   */
  render(p: p5, pose: PoseData, config: VisualConfig): void {
    if (!pose.detected) {
      return;
    }

    const { landmarks } = pose;
    const width = p.width;
    const height = p.height;

    // Set drawing style
    p.stroke(config.primaryColor.r, config.primaryColor.g, config.primaryColor.b, config.primaryColor.a * 255);
    p.strokeWeight(config.stickFigureThickness);

    // Detect debug mode by checking alpha value
    // Validates Requirements 3.2.25
    const isDebugMode = config.primaryColor.a < 0.7;

    // Draw head as ellipse based on ear positions
    // Requirements 3.2.2.1, 3.2.2.2, 3.2.2.3
    if (landmarks.nose.visibility >= 0.5) {
      const noseX = landmarks.nose.x * width;
      const noseY = landmarks.nose.y * height;
      
      // Check if both ears are visible for ellipse rendering
      const leftEarVisible = landmarks.leftEar.visibility >= 0.5;
      const rightEarVisible = landmarks.rightEar.visibility >= 0.5;
      
      if (isDebugMode) {
        // Debug mode: render head as outline only (no fill)
        p.noFill();
      } else {
        // Normal mode: render head as filled ellipse
        p.fill(config.primaryColor.r, config.primaryColor.g, config.primaryColor.b, config.primaryColor.a * 255);
      }
      
      if (leftEarVisible && rightEarVisible) {
        // Calculate ellipse width as distance between ears
        const earDistance = distance(landmarks.leftEar, landmarks.rightEar);
        const ellipseWidth = earDistance * width; // Convert to screen coordinates
        
        // Calculate ellipse height as 1.3x the width for natural proportions
        const ellipseHeight = ellipseWidth * 1.3;
        
        // Draw ellipse centered on nose
        p.ellipse(noseX, noseY, ellipseWidth, ellipseHeight);
      } else {
        // Fallback to circle if ears are not visible
        const headRadius = 20; // Fixed radius for fallback circle
        p.circle(noseX, noseY, headRadius * 2);
      }
      
      p.noFill();
    }

    // Draw neck (nose to shoulders midpoint)
    if (landmarks.nose.visibility >= 0.5 && 
        landmarks.leftShoulder.visibility >= 0.5 && 
        landmarks.rightShoulder.visibility >= 0.5) {
      const noseX = landmarks.nose.x * width;
      const noseY = landmarks.nose.y * height;
      const shoulderMidX = ((landmarks.leftShoulder.x + landmarks.rightShoulder.x) / 2) * width;
      const shoulderMidY = ((landmarks.leftShoulder.y + landmarks.rightShoulder.y) / 2) * height;
      
      p.line(noseX, noseY, shoulderMidX, shoulderMidY);
    }

    // Draw torso as filled shape
    this.drawTorso(p, landmarks, width, height, config, isDebugMode);

    // Draw left arm
    this.drawConnection(
      p,
      landmarks.leftShoulder.x,
      landmarks.leftShoulder.y,
      landmarks.leftShoulder.visibility,
      landmarks.leftElbow.x,
      landmarks.leftElbow.y,
      landmarks.leftElbow.visibility,
      width,
      height
    );

    this.drawConnection(
      p,
      landmarks.leftElbow.x,
      landmarks.leftElbow.y,
      landmarks.leftElbow.visibility,
      landmarks.leftWrist.x,
      landmarks.leftWrist.y,
      landmarks.leftWrist.visibility,
      width,
      height
    );

    // Draw right arm
    this.drawConnection(
      p,
      landmarks.rightShoulder.x,
      landmarks.rightShoulder.y,
      landmarks.rightShoulder.visibility,
      landmarks.rightElbow.x,
      landmarks.rightElbow.y,
      landmarks.rightElbow.visibility,
      width,
      height
    );

    this.drawConnection(
      p,
      landmarks.rightElbow.x,
      landmarks.rightElbow.y,
      landmarks.rightElbow.visibility,
      landmarks.rightWrist.x,
      landmarks.rightWrist.y,
      landmarks.rightWrist.visibility,
      width,
      height
    );

    // Draw left leg
    this.drawConnection(
      p,
      landmarks.leftHip.x,
      landmarks.leftHip.y,
      landmarks.leftHip.visibility,
      landmarks.leftKnee.x,
      landmarks.leftKnee.y,
      landmarks.leftKnee.visibility,
      width,
      height
    );

    this.drawConnection(
      p,
      landmarks.leftKnee.x,
      landmarks.leftKnee.y,
      landmarks.leftKnee.visibility,
      landmarks.leftAnkle.x,
      landmarks.leftAnkle.y,
      landmarks.leftAnkle.visibility,
      width,
      height
    );

    // Draw right leg
    this.drawConnection(
      p,
      landmarks.rightHip.x,
      landmarks.rightHip.y,
      landmarks.rightHip.visibility,
      landmarks.rightKnee.x,
      landmarks.rightKnee.y,
      landmarks.rightKnee.visibility,
      width,
      height
    );

    this.drawConnection(
      p,
      landmarks.rightKnee.x,
      landmarks.rightKnee.y,
      landmarks.rightKnee.visibility,
      landmarks.rightAnkle.x,
      landmarks.rightAnkle.y,
      landmarks.rightAnkle.visibility,
      width,
      height
    );
  }
}
